import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ColDef, GridOptions, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import Box from '@mui/material/Box'
import { ExportsModel, ExportStatus, ExportType } from '@archax/shared-types'
import { Button, Card, Stack, Typography } from '@mui/material'
import { Container } from '@mui/system'
import { downloadExportFile, getAllExports } from '../../api/exports'
import { ServerSideGrid } from '../../components/ServerSideGrid'
import { formatDate } from '../../util/formatters'

export default function ListExports() {
  const { t } = useTranslation()

  const columnDefs: ColDef<ExportsModel>[] = useMemo(
    () => [
      {
        field: 'type',
        headerName: t('headers.common.type'),
        valueGetter: (params: ValueGetterParams<ExportsModel>) => {
          return t(`exports.type.${params.data!.type}` as any)
        },
        sortable: true,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: Object.values(ExportType),
          valueFormatter: (params: ValueFormatterParams<ExportsModel>) => t(`exports.type.${params.value}` as any),
        },
      },
      {
        field: 'fileName',
        headerName: t('headers.common.fileName'),
        minWidth: 300,
        sortable: false,
      },
      {
        field: 'created_by',
        headerName: t('headers.common.createdBy'),
        minWidth: 100,
        sortable: false,
        valueGetter: (params: ValueGetterParams<ExportsModel>) => {
          return params.data!.creator?.name
        },
      },
      {
        field: 'created_at',
        headerName: t('headers.common.createdAt'),
        flex: 2,
        sortable: true,
        valueGetter: (params: ValueGetterParams<ExportsModel>) => {
          return formatDate(params.data!.createdAt?.toString())
        },
      },
      {
        field: 'status',
        headerName: t('headers.common.status'),
        sortable: false,
        valueGetter: (params: ValueGetterParams<ExportsModel>) => {
          return t(`exports.status.${params.data!.status}` as any)
        },
      },
      {
        field: 'actions',
        minWidth: 200,
        sortable: false,
        cellRenderer: (params: ICellRendererParams<ExportsModel>) => {
          if (params.data!.status !== ExportStatus.Ready) {
            return ''
          }
          return (
            <Stack direction={'row'}>
              <Box marginRight={1}>
                <Button
                  data-testid="list-exports_download-button"
                  variant="contained"
                  size="small"
                  onClick={() => {
                    ;(async () => {
                      await downloadExportFile(params.data!.id, params.data!.fileName)
                    })()
                  }}
                >
                  {t('exports.buttons.download')}
                </Button>
              </Box>
            </Stack>
          )
        },
      },
    ],
    [t],
  )

  const gridOptions: GridOptions = useMemo(
    () => ({
      columnDefs,
      floatingFilter: true,
      defaultColDef: { flex: 1 },
      onGridReady: (params) => {
        params.api?.sizeColumnsToFit()
      },
    }),
    [columnDefs],
  )

  return (
    <Container data-testid="list-exports" maxWidth="xl">
      <Card sx={{ p: 7 }}>
        <Typography data-testid="list-exports__title" align="left" variant="h3">
          {t('exports.getAll.title')}
        </Typography>
        <Box sx={{ width: '100%', marginTop: 3, marginBottom: 3 }}>
          <ServerSideGrid gridOptions={gridOptions} queryFn={getAllExports} queryParamsEnabled showOverlayLoading />
        </Box>
      </Card>
    </Container>
  )
}
